import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Per-page SEO + social meta wrapper.
 *
 * The CRA template's static index.html provides defaults for the whole site;
 * this component overrides them on a per-page basis. Search engines and
 * social platforms (LinkedIn, Twitter/X, Facebook) read these tags to
 * decide how to title/preview each page.
 *
 * Usage:
 *   <SEO
 *     title="Pricing — id100"
 *     description="Choose a plan: single report, monthly subscription, or enterprise."
 *     path="/pricing"
 *   />
 */
const SITE_NAME = 'id100';
const SITE_URL  = 'https://id100.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

const SEO = ({ title, description, path = '', image, type = 'website' }) => {
  const fullTitle = title || `${SITE_NAME} — AI-powered due diligence reports`;
  const fullDesc  = description || 'Generate verified investigative reports on companies and individuals. AI-powered, source-cited, blockchain-audited.';
  const canonical = `${SITE_URL}${path}`;
  const ogImage   = image || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={fullDesc} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph (Facebook, LinkedIn, etc.) */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type"      content={type} />
      <meta property="og:url"       content={canonical} />
      <meta property="og:title"     content={fullTitle} />
      <meta property="og:description" content={fullDesc} />
      <meta property="og:image"     content={ogImage} />

      {/* Twitter / X */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:url"         content={canonical} />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={fullDesc} />
      <meta name="twitter:image"       content={ogImage} />
    </Helmet>
  );
};

export default SEO;
